import { Button } from '@askable/ui/core/button';
import { useRef, useState } from 'react';
import { useMutation, useQuery } from 'urql';

import { SessionPages } from './data/SessionPages.query';
import { SaveSessionPages } from './data/SaveSessionPages.mutation';
import { pageLabels, pageMode, placements } from './pages';
import { SessionPagesForm } from './SessionPagesForm';
import type { Pages } from './pages';

export function SessionPagesPanel({ bookingId, sessionId }: { bookingId: string; sessionId: string }) {
  const [{ data, fetching, error: loadError }, refresh] = useQuery({
    query: SessionPages,
    variables: { bookingId },
    requestPolicy: 'network-only',
  });
  const [{ fetching: saving }, save] = useMutation(SaveSessionPages);
  const [saved, setSaved] = useState<Pages | null>(null);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  const editButton = useRef<HTMLButtonElement>(null);
  const session = data?.bookingByID?.session?.find(item => item?._id === sessionId);
  const pages: Pages = saved ?? session?.session_pages ?? {};
  const defaults: Pages = data?.bookingByID?.config?.session_pages ?? {};
  const close = () => {
    setEditing(false);
    setError(null);
    requestAnimationFrame(() => editButton.current?.focus());
  };
  const onSave = async (next: Pages) => {
    setError(null);
    try {
      const result = await save({ bookingId, sessionId, pages: next });
      if (result.error || !result.data?.updateBookingSessionPages) {
        setError(result.error?.message ?? 'Pages could not be saved. Please try again.');
        return;
      }
      setSaved(result.data.updateBookingSessionPages);
      close();
      setNotice('Pages saved for this session.');
    } catch {
      setError('Pages could not be saved. Please try again.');
    }
  };
  return (
    <section className="mt-5 border-t pt-4" aria-label="Participant pages">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold">Participant pages</h3>
        {!editing && session && !fetching && !loadError && (
          <Button
            ref={editButton}
            onClick={() => {
              setEditing(true);
              setNotice('');
            }}
          >
            Edit
          </Button>
        )}
      </div>
      {fetching && <p role="status">Loading pages…</p>}
      {!fetching && (loadError || !session) && (
        <div role="alert">
          <p>Session pages could not be loaded.</p>
          <Button onClick={() => refresh({ requestPolicy: 'network-only' })}>Retry</Button>
        </div>
      )}
      {!fetching && !loadError && session && editing && (
        <SessionPagesForm
          pages={pages}
          defaults={defaults}
          saving={saving}
          error={error}
          onCancel={close}
          onSave={next => {
            void onSave(next);
          }}
        />
      )}
      {!fetching && !loadError && session && !editing && (
        <dl className="space-y-3">
          {placements.map(p => {
            const mode = pageMode(pages[p]);
            const page = mode === 'default' ? defaults[p] : pages[p];
            const summaries = { default: 'Study default', off: 'No page', custom: 'Custom URL' };
            return (
              <div key={p} className="grid grid-cols-[100px_minmax(0,1fr)] gap-3 text-sm">
                <dt>{pageLabels[p]}</dt>
                <dd>
                  {summaries[mode]}
                  <p className="break-all text-xs text-muted-foreground">
                    {page?.url || (mode === 'default' ? 'No study default configured' : '')}
                  </p>
                </dd>
              </div>
            );
          })}
        </dl>
      )}
      <p role="status" className="mt-2 text-xs text-muted-foreground">
        {notice}
      </p>
    </section>
  );
}
